import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  ClipboardList, Truck, PackageCheck, ShoppingCart, MessageSquareWarning,
  UserSquare, History, Download, BookOpen,
} from "lucide-react";
import { toast } from "sonner";

// Panduan lengkap semua fitur Portal Mitra
export const GUIDE_SECTIONS = [
  {
    key: "opname",
    icon: PackageCheck,
    title: "1. Stok Opname (Hitung Stok Fisik)",
    steps: [
      "Buka menu \"Stok Opname & Keluhan\". Opname bulan berjalan merekonsiliasi penjualan BULAN LALU, dan hanya bisa dikirim setelah Supplier menginput laporan penjualan retail bulan lalu untuk toko Anda (Fase 2), paling lambat tanggal 10.",
      "Klik tombol \"Input Stok Opname\". Isi jumlah stok fisik yang benar-benar ada di toko untuk tiap produk.",
      "Kolom \"Selisih\" otomatis membandingkan stok fisik Anda dengan saldo sistem (hijau = lebih, merah = kurang).",
      "Unggah foto bukti fisik (dari galeri atau kamera), tambahkan catatan bila perlu, lalu klik \"Kirim ke Supplier\".",
      "Jika ada selisih, sistem otomatis membuat permintaan review ke Supplier. Hasilnya muncul di tab \"Keluhan Saya\".",
    ],
  },
  {
    key: "rekap",
    icon: ClipboardList,
    title: "2. Rekap Stok",
    steps: [
      "Tab \"Rekap Stok\" menampilkan ringkasan per produk: Total Diterima, Sudah Dilaporkan terjual, dan Saldo Sistem saat ini.",
      "Gunakan ini untuk cek cepat berapa sisa stok titipan yang masih ada di toko Anda.",
    ],
  },
  {
    key: "riwayat_stok",
    icon: History,
    title: "3. Riwayat Stok (Kartu Stok Gabungan)",
    steps: [
      "Tab \"Riwayat Stok\" menggabungkan Stok Awal, Pengiriman ke retail, dan Penjualan ke retail dalam SATU tabel kronologis.",
      "Kolom \"Saldo\" menunjukkan saldo berjalan produk setelah tiap pergerakan.",
      "Klik judul kolom untuk mengurutkan (tanggal, produk, masuk, keluar, dll). Gunakan kotak cari untuk memfilter.",
      "Klik tombol \"Excel\" atau \"PDF\" untuk mengunduh seluruh riwayat.",
    ],
  },
  {
    key: "pemesanan",
    icon: ShoppingCart,
    title: "4. Pemesanan Stok",
    steps: [
      "Buka menu \"Pemesanan Stok\" untuk meminta pengiriman barang dari Supplier.",
      "Tambahkan produk + jumlah + catatan per item, tentukan target tanggal kirim, lalu kirim.",
      "Status pesanan: Menunggu (pending), Disetujui, atau Ditolak. Pesanan yang masih pending bisa Anda batalkan.",
      "Catatan: persetujuan pesanan TIDAK otomatis mengirim stok — Supplier akan membuat pengiriman manual.",
    ],
  },
  {
    key: "keluhan",
    icon: MessageSquareWarning,
    title: "5. Kirim Keluhan / Pertanyaan",
    steps: [
      "Klik tombol \"Kirim Keluhan\" di kanan atas untuk menyampaikan keluhan pengiriman, retur, kualitas produk, atau lainnya.",
      "Pilih kategori dan prioritas, tulis subjek & pesan, lalu kirim.",
      "Balasan dari Supplier dan notifikasi hasil review opname muncul di tab \"Keluhan Saya\".",
    ],
  },
  {
    key: "identitas",
    icon: UserSquare,
    title: "6. Identitas Retail",
    steps: [
      "Lengkapi menu \"Identitas Retail\": nama PIC, nomor WA, kontak, alamat, dan jam operasional.",
      "Data ini penting agar koordinasi pengiriman dan pembayaran dengan Supplier berjalan lancar.",
    ],
  },
  {
    key: "riwayat_opname",
    icon: Truck,
    title: "7. Riwayat Opname & Kiriman",
    steps: [
      "Menu \"Riwayat Opname\" menyimpan semua opname yang pernah Anda kirim beserta status review-nya.",
      "Tab \"Riwayat Kiriman\" mencatat setiap pengiriman barang dari Supplier ke toko Anda.",
    ],
  },
];

const GUIDE_STORAGE_KEY = "hotto_mitra_guide_seen_v1";

export function shouldAutoShowGuide() {
  try { return !localStorage.getItem(GUIDE_STORAGE_KEY); } catch { return false; }
}

export function markGuideSeen() {
  try { localStorage.setItem(GUIDE_STORAGE_KEY, "1"); } catch { /* noop */ }
}

async function downloadGuidePdf() {
  try {
    const { jsPDF } = await import("jspdf");
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const marginX = 14;
    let y = 18;
    const lineH = 5.4;
    const pageH = doc.internal.pageSize.getHeight();

    const ensure = (need) => {
      if (y + need > pageH - 14) { doc.addPage(); y = 18; }
    };

    doc.setFont("helvetica", "bold");
    doc.setFontSize(17);
    doc.text("Panduan Portal Mitra Toko", marginX, y);
    y += 7;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(90);
    doc.text("Hotto Branch Cianjur — Gut Bless You", marginX, y);
    doc.setTextColor(0);
    y += 9;

    GUIDE_SECTIONS.forEach((sec) => {
      ensure(14);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      const titleLines = doc.splitTextToSize(sec.title, 182);
      doc.text(titleLines, marginX, y);
      y += titleLines.length * lineH + 1;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      sec.steps.forEach((st, i) => {
        const text = `${i + 1}. ${st}`;
        const lines = doc.splitTextToSize(text, 178);
        ensure(lines.length * lineH);
        doc.text(lines, marginX + 3, y);
        y += lines.length * lineH;
      });
      y += 4;
    });

    ensure(10);
    doc.setDrawColor(200);
    doc.line(marginX, y, 196, y);
    y += 6;
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text(`Dicetak: ${new Date().toLocaleString("id-ID")}`, marginX, y);

    doc.save(`Panduan_Portal_Mitra_${new Date().toISOString().slice(0, 10)}.pdf`);
    toast.success("Panduan diunduh (PDF)");
  } catch (e) {
    toast.error("Gagal mengunduh panduan: " + (e.message || e));
  }
}

export default function MitraGuide({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="mitra-guide-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-[var(--hotto-primary)]" />
            Panduan Penggunaan Portal Mitra
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Selamat datang di Portal Mitra Toko Binaan <b>Hotto Branch Cianjur</b>. Berikut panduan singkat
            untuk menggunakan semua fitur. Anda bisa mengunduh panduan ini sebagai PDF.
          </p>
          {GUIDE_SECTIONS.map((sec) => {
            const Icon = sec.icon;
            return (
              <div key={sec.key} className="border border-[var(--hotto-border)] rounded-md p-3 bg-slate-50/50">
                <div className="flex items-center gap-2 font-semibold text-slate-800 mb-2">
                  <span className="w-7 h-7 rounded-md bg-[var(--hotto-primary)]/10 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-[var(--hotto-primary)]" />
                  </span>
                  {sec.title}
                </div>
                <ol className="list-decimal ml-6 space-y-1 text-sm text-slate-600">
                  {sec.steps.map((s, i) => <li key={i}>{s}</li>)}
                </ol>
              </div>
            );
          })}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={downloadGuidePdf} data-testid="btn-download-guide" className="border-emerald-400 text-emerald-700 hover:bg-emerald-50">
            <Download className="w-4 h-4 mr-2" /> Unduh Panduan (PDF)
          </Button>
          <Button onClick={() => onOpenChange(false)} data-testid="btn-close-guide" className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)]">
            Mengerti
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
